// yyyyMMdd | yyyy-MM-dd | yyyy-MM-ddT00:00:00 | yyyy-MM-ddT00:00:00Z
const CUSTOM_FORMAT_DATE_REGEXP = /^(\d{4})-?(\d{2})-?(\d{2})(T00:00:00Z?)?$/;

/**
 * Parses given string to a Date object.
 * Parsing logic is the same as standard Date.parse,
 * with one addition that strings formatted as 'yyyyMMdd' or 'yyyy-MM-dd'
 * are treated as though they represent local time.
 * @param {string} str A string to be parsed.
 * @return {Date} Parsed date object.
 */
function parseDateString(str: string): Date | null {
    if (typeof str !== 'string') {
        return null;
    }
    const parsed = str.match(CUSTOM_FORMAT_DATE_REGEXP);
    if (!parsed) {
        return new Date(str);
    }

    const year = parseInt(parsed[1], 10);
    const month = parseInt(parsed[2], 10) - 1; // month is zero based;
    const day = parseInt(parsed[3], 10);

    if ([year, month, day].some(isNaN)) {
        return null;
    }

    // When called as a constructor with more than one argument,
    // the specifed arguments represent local time
    return new Date(year, month, day);
}

/**
 * Formats the local date parts of the given Date as 'yyyy-MM-ddT00:00:00Z'.
 *
 * The inverse of {@link parseDateString}: that function reads the date-only formats as local time, so
 * a value round-trips through this one unchanged. Use it for date-only values (issue dates and the
 * like) — `toISOString` would report the previous/next day for any non-UTC timezone.
 * @param {Date} date A local-time date.
 * @return {string} Date as "yyyy-MM-ddT00:00:00Z"
 */
function formatDateString(date: Date): string {
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    return `${year}-${month}-${day}T00:00:00Z`;
}

/**
 * Returns today's date in ISO format (YYYY-MM-DD) based on local timezone.
 * Useful for API filters that require DateOnly format.
 * @return {string} Today's date as "YYYY-MM-DD"
 */
function getTodayDateString(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = ('0' + (today.getMonth() + 1)).slice(-2);
    const day = ('0' + today.getDate()).slice(-2);
    return `${year}-${month}-${day}`;
}

export { formatDateString, getTodayDateString, parseDateString };
